import { Box, Text } from "@chakra-ui/react";
import React from "react";
import EntityProfileActionBar from "../../../../entityProfileModal/components/EntityProfileActionBar";
import EntityModalButton from "../../../../entityProfileModal/components/EntityModalButton";
import {
  entityProfileModalBorders,
  entityProfileModalColors,
} from "../../../../entityProfileModal/entityProfileModal.theme";
import type { CaregiverProfileCaregiver } from "../../../caregiverProfileModal.types";

type Props = {
  caregiver: CaregiverProfileCaregiver;
};

type AngularWindow = Window &
  typeof globalThis & {
    angular?: {
      element: (element: Element | Document) => {
        injector?: () => AngularInjector;
      };
    };
  };

type AngularInjector = {
  get: <T = unknown>(name: string) => T;
};

type AngularScope = {
  $applyAsync: () => void;
  $destroy: () => void;
  caregiver: CaregiverProfileCaregiver;
};

type AngularRootScope = {
  $evalAsync: (callback: () => void) => void;
  $new: (isolated: boolean) => AngularScope;
};

type AngularCompile = (element: Element) => (scope: AngularScope) => void;

type UibModal = {
  open: (params: {
    controller: string;
    resolve: {
      caregiverId: () => CaregiverProfileCaregiver["id"];
    };
    size: string;
    templateUrl: string;
  }) => void;
};

type TrainingCenterAddBundleModalService = {
  getBundleAddedEmitter: () => {
    addBundle: () => void;
  };
};

type OrientationCenterService = {
  addMissingOrientations: (caregiverId: CaregiverProfileCaregiver["id"]) => Promise<unknown>;
};

type Toaster = {
  pop: (type: "error" | "success", title: string, message?: string) => void;
};

const trainingCenterHostStyleId = "react-caregiver-training-center-host-styles";
let trainingCenterHostStyleReferences = 0;

function getAngularInjector() {
  const angular = (window as AngularWindow).angular;

  return angular?.element(document.body).injector?.();
}

function mountTrainingCenterHostStyles() {
  trainingCenterHostStyleReferences += 1;

  if (document.getElementById(trainingCenterHostStyleId) === null) {
    const style = document.createElement("style");
    style.id = trainingCenterHostStyleId;
    style.textContent = `
.react-caregiver-training-center-host {
  min-width: 0;
  width: 100%;
}

.react-caregiver-training-center-host caregiver-training-center {
  display: block;
  min-width: 0;
  width: 100%;
}

.react-caregiver-training-center-host .content-body,
.react-caregiver-training-center-host .row:last-child,
.react-caregiver-training-center-host .panel:last-child {
  margin-bottom: 0;
}

.react-caregiver-training-center-host .panel {
  border-radius: 2px;
}

.react-caregiver-training-center-host .panel-body {
  overflow-x: auto;
}

.react-caregiver-training-center-host .dropdown-menu,
.react-caregiver-training-center-host .popover {
  z-index: 1065;
}

body > .modal .ctc-bundle-header {
  min-width: 0;
}
`;
    document.head.appendChild(style);
  }

  return () => {
    trainingCenterHostStyleReferences = Math.max(0, trainingCenterHostStyleReferences - 1);

    if (trainingCenterHostStyleReferences === 0) {
      document.getElementById(trainingCenterHostStyleId)?.remove();
    }
  };
}

export default function CaregiverProfileTrainingCenterSection(props: Props) {
  const hostRef = React.useRef<HTMLDivElement | null>(null);
  const [isAngularUnavailable, setIsAngularUnavailable] = React.useState(false);
  const caregiverId = props.caregiver.id;
  const showAddMissingOrientation = props.caregiver.status === "PENDING";

  React.useEffect(() => mountTrainingCenterHostStyles(), []);

  React.useEffect(() => {
    const host = hostRef.current;
    const injector = getAngularInjector();

    if (host === null || injector === undefined) {
      setIsAngularUnavailable(true);
      return undefined;
    }

    setIsAngularUnavailable(false);

    const rootScope = injector.get<AngularRootScope>("$rootScope");
    const compile = injector.get<AngularCompile>("$compile");
    const scope = rootScope.$new(true);
    const element = document.createElement("caregiver-training-center");

    scope.caregiver = props.caregiver;
    element.setAttribute("caregiver", "caregiver");

    host.replaceChildren(element);
    compile(element)(scope);
    scope.$applyAsync();

    return () => {
      scope.$destroy();
      host.replaceChildren();
    };
  }, [props.caregiver]);

  const openAddBundleModal = React.useCallback(() => {
    const injector = getAngularInjector();

    if (injector === undefined) {
      setIsAngularUnavailable(true);
      return;
    }

    const rootScope = injector.get<AngularRootScope>("$rootScope");
    const uibModal = injector.get<UibModal>("$uibModal");

    rootScope.$evalAsync(() => {
      uibModal.open({
        templateUrl: "admin/views/caregiverTrainingCenterAddBundleModal.html",
        size: "lg",
        controller: "caregiverTrainingCenterAddBundleModalCtrl",
        resolve: {
          caregiverId: () => caregiverId,
        },
      });
    });
  }, [caregiverId]);

  const addMissingOrientation = React.useCallback(() => {
    const injector = getAngularInjector();

    if (injector === undefined) {
      setIsAngularUnavailable(true);
      return;
    }

    const rootScope = injector.get<AngularRootScope>("$rootScope");
    const orientationCenterService = injector.get<OrientationCenterService>(
      "OrientationCenterService"
    );
    const addBundleModalService = injector.get<TrainingCenterAddBundleModalService>(
      "caregiverTrainingCenterAddBundleModalService"
    );
    const toaster = injector.get<Toaster>("toaster");

    rootScope.$evalAsync(() => {
      orientationCenterService
        .addMissingOrientations(caregiverId)
        .then(() => addBundleModalService.getBundleAddedEmitter().addBundle())
        .catch(() => toaster.pop("error", "Couldn't add orientation", "Please try again later"));
    });
  }, [caregiverId]);

  if (isAngularUnavailable) {
    return (
      <Box
        bg={entityProfileModalColors.surfaceMuted}
        border={entityProfileModalBorders.panel}
        borderRadius="2px"
        color={entityProfileModalColors.secondaryText}
        px={4}
        py={5}
      >
        <Text>Training Center is available only inside the Angular shell.</Text>
      </Box>
    );
  }

  return (
    <Box minW={0}>
      <EntityProfileActionBar mb={3}>
        {showAddMissingOrientation ? (
          <EntityModalButton
            kind="blue"
            size="sm"
            variant="outline"
            onClick={addMissingOrientation}
          >
            Add Missing Orientation
          </EntityModalButton>
        ) : null}
        <EntityModalButton kind="blue" size="sm" onClick={openAddBundleModal}>
          Add Bundle
        </EntityModalButton>
      </EntityProfileActionBar>
      <Box ref={hostRef} className="react-caregiver-training-center-host" minH="160px" />
    </Box>
  );
}
